/**
 * Custom Split Editor Component
 * 
 * Modal for editing custom split amounts
 */

import { useState, useEffect, useMemo } from 'react';
import { View, Modal, ScrollView, Pressable, TextInput, Alert, KeyboardAvoidingView, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Caption } from '@/components/ui/Text';
import { InputField } from '@/components/ui/InputField';
import { formatCurrency } from '@/utils/currency';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type CustomSplitEditorProps = {
  visible: boolean;
  onClose: () => void;
  amount: number;
  currency: string;
  tripMembers: Array<{ userId: string; name?: string; email: string }>;
  currentUserId: string;
  initialAmounts: Record<string, number>;
  onAmountsChange: (amounts: Record<string, number>) => void;
};

export function CustomSplitEditor({
  visible,
  onClose,
  amount,
  currency,
  tripMembers,
  currentUserId,
  initialAmounts,
  onAmountsChange,
}: CustomSplitEditorProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const initialAmountsKey = useMemo(
    () =>
      JSON.stringify(
        Object.keys(initialAmounts)
          .sort()
          .reduce<Record<string, number>>((acc, k) => {
            const v = initialAmounts[k];
            acc[k] = Math.round(v * 100) / 100;
            return acc;
          }, {}),
      ),
    [initialAmounts],
  );

  const memberIdsKey = useMemo(
    () => tripMembers.map((m) => m.userId).sort().join(','),
    [tripMembers],
  );

  useEffect(() => {
    if (!visible) return;
    const initial: Record<string, string> = {};
    tripMembers.forEach((member) => {
      if (initialAmounts[member.userId] !== undefined) {
        initial[member.userId] = initialAmounts[member.userId].toString();
      } else {
        initial[member.userId] = '';
      }
    });
    setAmounts(initial);
    setErrors({});
  }, [visible, initialAmountsKey, memberIdsKey]);

  const handleAmountChange = (userId: string, value: string) => {
    // Allow any input - don't filter on every keystroke
    // This allows users to freely edit, delete, and clear the field
    // We'll validate on save instead
    setAmounts({ ...amounts, [userId]: value });

    // Clear error for this field when user starts typing
    if (errors[userId]) {
      const newErrors = { ...errors };
      delete newErrors[userId];
      setErrors(newErrors);
    }
  };

  const handleSave = () => {
    const numericAmounts: Record<string, number> = {};
    const newErrors: Record<string, string> = {};
    let total = 0;

    tripMembers.forEach((member) => {
      const value = amounts[member.userId] || '';
      
      // Trim and filter to only numbers and decimal point for validation
      const cleanedValue = value.trim().replace(/[^0-9.]/g, '');
      
      if (!cleanedValue || cleanedValue === '') {
        newErrors[member.userId] = 'Amount is required';
        return;
      }
      
      const numValue = parseFloat(cleanedValue);

      if (isNaN(numValue) || numValue <= 0) {
        newErrors[member.userId] = 'Must be greater than 0';
        return;
      }

      numericAmounts[member.userId] = numValue;
      total += numValue;
    });

    // Check if total matches expense amount
    const difference = Math.abs(total - amount);
    if (difference >= 0.01) {
      Alert.alert(
        'Invalid Amounts',
        `Total split amounts (${formatCurrency(total, currency)}) must equal expense amount (${formatCurrency(amount, currency)}). Difference: ${formatCurrency(difference, currency)}`
      );
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onAmountsChange(numericAmounts);
    onClose();
  };

  const handleEqualSplit = () => {
    const perPerson = amount / tripMembers.length;
    const equalAmounts: Record<string, string> = {};
    tripMembers.forEach((member) => {
      equalAmounts[member.userId] = perPerson.toFixed(2);
    });
    setAmounts(equalAmounts);
    setErrors({});
  };

  const getTotal = () => {
    return Object.values(amounts).reduce((sum, val) => {
      // Clean the value to extract numeric content
      // Only count non-empty values
      if (!val || val.trim() === '') return sum;
      const cleanedValue = val.trim().replace(/[^0-9.]/g, '');
      if (!cleanedValue) return sum;
      const num = parseFloat(cleanedValue);
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
  };

  const getMemberName = (userId: string): string => {
    const member = tripMembers.find((m) => m.userId === userId);
    if (member) {
      return member.name || member.email.split('@')[0];
    }
    return userId === currentUserId ? 'You' : 'Unknown';
  };

  const total = getTotal();
  const difference = Math.abs(total - amount);
  const isValid = difference < 0.01 && Object.keys(errors).length === 0;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
      presentationStyle="pageSheet"
    >
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.modalOverlay,
          justifyContent: 'flex-end',
        }}
      >
        <Pressable style={{ flex: 1 }} onPress={onClose} />
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'height' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? insets.bottom : 0}
        >
          <View
            style={{
              width: '100%',
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: theme.radii.xl,
              borderTopRightRadius: theme.radii.xl,
              maxHeight: '90%',
            }}
          >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: theme.spacing(2),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderMuted,
            }}
          >
            <SubHeading>Custom Split Amounts</SubHeading>
            <Pressable onPress={onClose}>
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          </View>

          <ScrollView
            contentContainerStyle={{ padding: theme.spacing(2), gap: theme.spacing(2), paddingBottom: theme.spacing(2) + insets.bottom }}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
          >
            <View
              style={{
                padding: theme.spacing(1.5),
                backgroundColor: theme.colors.primary + '08',
                borderRadius: theme.radii.md,
                marginBottom: theme.spacing(1),
              }}
            >
              <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(0.5) }}>
                Total expense: {formatCurrency(amount, currency)}
              </Caption>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Caption color={theme.colors.textSecondary}>Total split:</Caption>
                <ThemedText
                  variant="bodyStrong"
                  color={
                    isValid
                      ? theme.colors.success || '#34C759'
                      : theme.colors.error || '#FF3B30'
                  }
                >
                  {formatCurrency(total, currency)}
                </ThemedText>
              </View>
              {!isValid && (
                <Caption color={theme.colors.error || '#FF3B30'} style={{ marginTop: theme.spacing(0.5) }}>
                  Difference: {formatCurrency(difference, currency)}
                </Caption>
              )}
            </View>

            <Button
              label="Split Equally"
              variant="secondary"
              onPress={handleEqualSplit}
              icon={<Ionicons name="calculator-outline" size={18} color={theme.colors.text} />}
            />

            <View style={{ gap: theme.spacing(2) }}>
              {tripMembers.map((member) => {
                const displayName = getMemberName(member.userId);
                const isCurrentUser = member.userId === currentUserId;
                const value = amounts[member.userId] || '';
                const error = errors[member.userId];

                return (
                  <View key={member.userId} style={{ position: 'relative' }}>
                    <InputField
                      label={isCurrentUser ? 'You' : displayName}
                      value={value}
                      onChangeText={(text) => handleAmountChange(member.userId, text)}
                      keyboardType="decimal-pad"
                      placeholder="0.00"
                      errorText={error}
                      editable={true}
                      style={{ paddingRight: theme.spacing(8) }}
                    />
                    <View style={{ 
                      position: 'absolute', 
                      right: theme.spacing(2), 
                      top: theme.spacing(4.5),
                      pointerEvents: 'none'
                    }}>
                      <ThemedText variant="body" color={theme.colors.textSecondary}>
                        {currency}
                      </ThemedText>
                    </View>
                  </View>
                );
              })}
            </View>
          </ScrollView>

          <View
            style={{
              padding: theme.spacing(2),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
              gap: theme.spacing(1.5),
              paddingBottom: theme.spacing(2) + insets.bottom,
            }}
          >
            <Button
              label="Save"
              onPress={handleSave}
              disabled={!isValid}
            />
            <Button
              label="Cancel"
              variant="secondary"
              onPress={onClose}
            />
          </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}


